import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // MANDATORY honeypot field
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Venligst indtast dit navn.';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Venligst indtast dit telefonnummer.';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Venligst indtast din e-mailadresse.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Venligst indtast en gyldig e-mailadresse.';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Venligst skriv en beskrivelse af din opgave.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!validate()) return;

    setSubmitting(true);
    setServerError('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Honeypot field
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        setServerError(data.message || 'Der opstod en fejl under afsendelsen. Venligst prøv igen eller ring til os.');
      }
    } catch (err) {
      setServerError('Kunne ikke oprette forbindelse til serveren. Venligst prøv igen eller ring til Tlf. 22 23 10 10.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#dce2d7] text-[#1f241d]">
      <Helmet>
        <title>Kontakt - PR Entreprise & Anlæg ApS</title>
        <meta
          name="description"
          content="Kontakt PR Entreprise & Anlæg ApS i Ishøj for et uforpligtende tilbud på autoriseret kloak, belægning og anlægsarbejde. Tlf: 22 23 10 10, e-mail: info@prentreprise.dk."
        />
        <meta property="og:title" content="Kontakt - PR Entreprise & Anlæg ApS" />
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#141f14] text-white py-16 lg:py-20 border-b border-[#233323] overflow-hidden">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/prentreprise-1.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141f14] via-[#141f14]/90 to-transparent pointer-events-none" />
        <div className="relative max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4 animate-fade-in-up">
            <span className="text-[#9ecc49] text-xs font-bold uppercase tracking-widest bg-[#3d5229] px-3 py-1 rounded-none border border-[#9ecc49]/30">
              Kontakt Os
            </span>
            <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Indhent et Uforpligtende Tilbud
            </h1>
            <p className="text-[#e8eee6] text-base sm:text-lg leading-relaxed">
              Vi står klar til at rådgive dig og give et konkret tilbud på din entreprise-, anlægs- eller kloakopgave i Ishøj og omegn.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-[#dce2d7] text-[#1f241d]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Contact Details Card */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-[#141f14] text-white p-8 rounded-none border border-[#233323] shadow-lg space-y-6">
                <div className="border-b border-[#233323] pb-4">
                  <h2 className="font-heading font-extrabold text-xl sm:text-2xl text-white">
                    PR Entreprise & Anlæg ApS
                  </h2>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#9ecc49] mt-1 block">
                    Autoriseret Kloakmester
                  </span>
                </div>

                <div className="space-y-5 text-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-none bg-[#3d5229] text-[#9ecc49] flex items-center justify-center shrink-0 border border-[#9ecc49]/30">
                      <i className="fa-solid fa-location-dot text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#9ecc49] font-bold uppercase tracking-wider block">Adresse</span>
                      <p className="text-base text-white font-medium">Ishøj Bygade 90</p>
                      <p className="text-gray-300">2635 Ishøj</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-none bg-[#3d5229] text-[#9ecc49] flex items-center justify-center shrink-0 border border-[#9ecc49]/30">
                      <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#9ecc49] font-bold uppercase tracking-wider block">Telefon</span>
                      <a
                        href="tel:22231010"
                        className="text-lg text-white font-bold hover:text-[#9ecc49] transition-colors whitespace-nowrap block mt-0.5"
                      >
                        Tlf. 22 23 10 10
                      </a>
                      <a
                        href="tel:26791310"
                        className="text-xs text-gray-400 hover:text-white transition-colors block mt-0.5"
                      >
                        Sekundær: 26 79 13 10
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-none bg-[#3d5229] text-[#9ecc49] flex items-center justify-center shrink-0 border border-[#9ecc49]/30">
                      <i className="fa-regular fa-envelope text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#9ecc49] font-bold uppercase tracking-wider block">E-mail</span>
                      <a
                        href="mailto:info@prentreprise.dk"
                        className="text-base text-white hover:text-[#9ecc49] transition-colors whitespace-nowrap inline-block mt-0.5"
                      >
                        info@prentreprise.dk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-none bg-[#3d5229] text-[#9ecc49] flex items-center justify-center shrink-0 border border-[#9ecc49]/30">
                      <i className="fa-solid fa-id-card text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#9ecc49] font-bold uppercase tracking-wider block">CVR-nummer</span>
                      <span className="font-mono text-base font-semibold text-white bg-[#0c140c] px-3 py-1 rounded-none inline-block mt-0.5 border border-[#233323]">
                        43852507
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#233323] text-xs text-gray-400">
                  <p>Telefontid: Mandag – Fredag 07:00 – 17:00</p>
                  <p className="mt-1">Vi besvarer e-mails hurtigst muligt inden for 24 timer.</p>
                </div>
              </div>

              {/* Geographical area box */}
              <div className="bg-white p-6 rounded-none border border-[#cbd3c5] shadow-sm space-y-3 text-sm">
                <h3 className="font-heading font-bold text-base text-[#1f241d]">
                  Dækningsområde
                </h3>
                <p className="text-[#4e564a] leading-relaxed text-xs sm:text-sm">
                  Vi udfører opgaver i Ishøj, Greve, Vallensbæk, Taastrup, Glostrup, Albertslund, Brøndby, København og hele Storkøbenhavn samt det øvrige Sjælland.
                </p>
              </div>
            </div>

            {/* Form Section */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 sm:p-10 rounded-none border border-[#cbd3c5] shadow-sm space-y-6">
                <div>
                  <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-[#1f241d]">
                    Send os en besked
                  </h2>
                  <p className="text-[#4e564a] text-sm mt-1">
                    Udfyld formularen, så vender vi hurtigt tilbage med kompetent rådgivning eller et uforpligtende tilbud.
                  </p>
                </div>

                {submitted ? (
                  <div className="bg-[#141f14] text-white p-8 rounded-none border border-[#9ecc49] text-center space-y-4 animate-fadeIn">
                    <div className="w-16 h-16 rounded-none bg-[#3d5229] text-[#9ecc49] mx-auto flex items-center justify-center text-3xl border border-[#9ecc49]/50">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-white">Mange tak for din henvendelse!</h3>
                    <p className="text-gray-300 text-sm max-w-md mx-auto">
                      Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt. Du kan også ringe direkte på Tlf. 22 23 10 10.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      className="mt-4 bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] px-6 py-2.5 rounded-none font-heading font-bold text-xs tracking-wider uppercase transition-colors cursor-pointer"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    
                    {/* MANDATORY HONEYPOT FIELD (Absolute hidden from humans, text type) */}
                    <input
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="sr-only absolute -top-[9999px] -left-[9999px]"
                    />

                    {serverError && (
                      <div className="bg-red-50 text-red-700 p-4 rounded-none border border-red-200 text-sm flex items-center gap-2">
                        <i className="fa-solid fa-circle-exclamation shrink-0" aria-hidden="true" />
                        <span>{serverError}</span>
                      </div>
                    )}

                    <div>
                      <label htmlFor="name" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                        Fulde Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="F.eks. Anders Jensen"
                        className={`w-full px-4 py-3 rounded-none border bg-black/5 text-[#1f241d] focus:outline-none focus:ring-1 focus:ring-[#3d5229] focus:bg-white transition-colors ${
                          errors.name ? 'border-red-500 bg-red-50' : 'border-black/10'
                        }`}
                      />
                      {errors.name && <p className="text-red-600 text-xs mt-1.5 font-medium">{errors.name}</p>}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="F.eks. 22231010"
                          className={`w-full px-4 py-3 rounded-none border bg-black/5 text-[#1f241d] focus:outline-none focus:ring-1 focus:ring-[#3d5229] focus:bg-white transition-colors ${
                            errors.phone ? 'border-red-500 bg-red-50' : 'border-black/10'
                          }`}
                        />
                        {errors.phone && <p className="text-red-600 text-xs mt-1.5 font-medium">{errors.phone}</p>}
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                          E-mailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="F.eks. anders@eksempel.dk"
                          className={`w-full px-4 py-3 rounded-none border bg-black/5 text-[#1f241d] focus:outline-none focus:ring-1 focus:ring-[#3d5229] focus:bg-white transition-colors ${
                            errors.email ? 'border-red-500 bg-red-50' : 'border-black/10'
                          }`}
                        />
                        {errors.email && <p className="text-red-600 text-xs mt-1.5 font-medium">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-bold uppercase text-[#1f241d] mb-1.5">
                        Beskrivelse af opgaven <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Beskriv venligst hvilken opgave du ønsker udført (f.eks. belægning af ny indkørsel, jordarbejde eller haveanlæg)..."
                        className={`w-full px-4 py-3 rounded-none border bg-black/5 text-[#1f241d] focus:outline-none focus:ring-1 focus:ring-[#3d5229] focus:bg-white transition-colors resize-none ${
                          errors.message ? 'border-red-500 bg-red-50' : 'border-black/10'
                        }`}
                      />
                      {errors.message && <p className="text-red-600 text-xs mt-1.5 font-medium">{errors.message}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold py-4 px-6 rounded-none font-heading uppercase text-xs tracking-wider transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {submitting ? (
                        <>
                          <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                          <span>Sender besked...</span>
                        </>
                      ) : (
                        <>
                          <span>Send besked og få tilbud</span>
                          <i className="fa-solid fa-paper-plane text-xs text-[#9ecc49]" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
